import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
} from '@angular/core';
import { Card } from 'primeng/card';
import { FormBeneficiaryDetailsComponent } from '../../shared/components/beneficiary/form-beneficiary-details/form-beneficiary-details.component';
import { BaseFormComponent } from '../../core/components/base-form.component';
import { FamilyMemberFormModel } from './form-group/family-member-form.model';
import { FormGroup } from '@angular/forms';
import { FamilyMemberFormBuilder } from './form-group/family-member.form.builder';
import { CzlonekRodziny } from '../../shared/models/czlonek-rodziny';
import { FormAddress } from '../../shared/components/address/form-address/form-address';
import { FormFamilyMemberSchoolComponent } from './components/form-family-member-school/form-family-member-school.component';
import { Button } from 'primeng/button';
import { APP_PATHS } from '../../core/config/app-paths';
import { Router } from '@angular/router';
import { FormsUtils } from '../../shared/utils/forms.utils';
import { SpinnerService } from '../../core/services/spinner.service';
import { MessageService } from 'primeng/api';
import { UserApi } from '../../shared/services/api/user.api';
import { catchError, finalize, tap, throwError } from 'rxjs';
import { ToastSeverity } from '../../shared/enums/toast-severity.enum';
import { UserSettingsPage } from '../../shared/enums/user-settings-page.enum';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { InfoMessageComponent } from '../../shared/components/info-message/info-message.component';
import { HttpResponseService } from '../../core/services/http-error-response.service';

@Component({
  selector: 'app-family-member',
  imports: [
    Card,
    FormBeneficiaryDetailsComponent,
    FormAddress,
    FormFamilyMemberSchoolComponent,
    Button,
    InfoMessageComponent,
  ],
  providers: [UserApi],
  templateUrl: './family-member.component.html',
  styleUrl: './family-member.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FamilyMemberComponent extends BaseFormComponent<FamilyMemberFormModel> {
  protected router = inject(Router);
  readonly #spinnerService = inject(SpinnerService);
  readonly #httpResponseService = inject(HttpResponseService);
  readonly #messageService = inject(MessageService);
  readonly #userApi = inject(UserApi);
  readonly #destroyRef = inject(DestroyRef);

  protected familyMember = input<CzlonekRodziny | undefined>();

  constructor() {
    super();
    // Jezeli edycja to wyswietlamy wszystkie bledy na formatce
    afterNextRender(() => {
      if (this.familyMember()) {
        this.formGroup().markAllAsTouched();
      }
    });
  }

  protected override createFormGroup(): FormGroup<FamilyMemberFormModel> {
    return FamilyMemberFormBuilder.createFormGroup(this.familyMember()!);
  }

  protected navigateToFamilyMembers(): void {
    const url = `${APP_PATHS.userSettings.root}/${UserSettingsPage.FAMILY_MEMBERS}`;
    this.router.navigate([url]);
  }

  protected save(): void {
    if (!FormsUtils.isReadyForSave(this.formGroup())) {
      this.#messageService.add({
        severity: ToastSeverity.ERROR,
        summary: 'Wystąpił błąd podczas walidacji formularza',
      });
      return;
    }

    this.#spinnerService.show();
    this.#userApi
      .saveFamilyMember$(FamilyMemberFormBuilder.toModel(this.formGroup()))
      .pipe(
        tap(() => this.#handleSaveFamilyMemberSuccess()),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Wystąpił błąd podczas zapisu członka rodziny',
          );
          return throwError(() => err);
        }),
        takeUntilDestroyed(this.#destroyRef),
        finalize(() => {
          this.#spinnerService.hide();
        }),
      )
      .subscribe();
  }

  #handleSaveFamilyMemberSuccess(): void {
    this.#httpResponseService.showSuccessMessage(
      'Poprawnie zapisano członka rodziny',
    );
    this.navigateToFamilyMembers();
  }
}
